import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { extractErrorDetails } from '../../utils/errorUtils';

type EnvAccessStatus = { required: boolean; unlocked: boolean };

/** Builds whose backend may gate access — the frontend names for
 *  `env_access_allowlist.GATED_ENVS`.
 *
 *  This is the *only* thing known before any request comes back, and it is what
 *  lets the overlay paint with the app's first frame instead of one round trip
 *  later. Without it the login screen is visibly rendered first, which reads as
 *  an unprotected environment (QA FAIL, CIQ-1645). `/env-access/status` still
 *  decides whether the gate stays up — this only decides whether it starts up.
 *
 *  Deliberately not a guess at whether a gate is *in force*: it is a build-time
 *  fact about which API this bundle talks to. Over-including an environment
 *  costs one "checking" frame; under-including one costs the flash back.
 *
 *  `local` is absent on purpose. A developer testing the gate with `ENV=dev`
 *  locally keeps the async path, so `ng serve` never paints a card for the
 *  ungated environment it is normally pointed at.
 */
const MAYBE_GATED_BUILDS: readonly string[] = ['development', 'staging', 'feature1'];

/** Environment access gate for dev / staging / feature1 (CIQ-1645).
 *
 *  Sits in front of the whole app on the restricted environments: a QA engineer
 *  enters their address, receives a six-digit code at it, and enters the code.
 *  Past that, CollectivIQ behaves exactly as it does on prod — signup, guest
 *  mode and arbitrary throwaway test accounts all work, which is the entire
 *  reason this is a perimeter rather than an allowlist on app login.
 *
 *  Rendered as a full-screen overlay rather than a route so no route config,
 *  guard or redirect has to know about it, and so it also covers deep links.
 *  It is never shown on prod or local: `required` comes from the backend, and a
 *  build for an environment that has no gate never mounts the overlay at all
 *  (see `MAYBE_GATED_BUILDS`, which is also what keeps the app from flashing
 *  into view on one that does).
 *
 *  This screen is a convenience, not the security boundary. The API 511s every
 *  request until the cookie is present, so deleting this element from the DOM
 *  yields an app that cannot make a single call.
 */
@Component({
    selector: 'app-env-gate',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (visible()) {
            <div aria-labelledby="env-gate-title" aria-modal="true" class="env-gate" role="dialog">
                <form class="env-gate__card" (submit)="submit($event)">
                    <h1 class="env-gate__title" id="env-gate-title">Restricted environment</h1>

                    @if (step() === 'checking') {
                        <p class="env-gate__body" role="status">Checking environment access…</p>
                    } @else if (step() === 'email') {
                        <p class="env-gate__body">
                            This is a CollectivIQ test environment. Enter your approved work email and we'll send you a six-digit access
                            code.
                        </p>
                        <label class="env-gate__label" for="env-gate-email">Work email</label>
                        <input
                            autocomplete="email"
                            class="env-gate__input"
                            data-testid="env-gate-email"
                            id="env-gate-email"
                            name="email"
                            type="email"
                            [attr.aria-invalid]="error() ? 'true' : null"
                            [disabled]="busy()" />
                    } @else {
                        <p class="env-gate__body">
                            We sent a six-digit code to <strong>{{ email() }}</strong
                            >. It expires in 10 minutes.
                        </p>
                        <label class="env-gate__label" for="env-gate-code">Access code</label>
                        <input
                            autocomplete="one-time-code"
                            class="env-gate__input"
                            data-testid="env-gate-code"
                            id="env-gate-code"
                            inputmode="numeric"
                            name="code"
                            [attr.aria-invalid]="error() ? 'true' : null"
                            [disabled]="busy()" />
                    }

                    @if (error()) {
                        <p class="env-gate__error" data-testid="env-gate-error" role="alert">{{ error() }}</p>
                    }
                    @if (notice()) {
                        <p class="env-gate__notice" data-testid="env-gate-notice" role="status">{{ notice() }}</p>
                    }

                    @if (step() !== 'checking') {
                        <button class="env-gate__submit" data-testid="env-gate-submit" type="submit" [disabled]="busy()">
                            {{ busy() ? 'Working…' : step() === 'email' ? 'Send code' : 'Continue' }}
                        </button>
                    }

                    @if (step() === 'code') {
                        <button class="env-gate__back" data-testid="env-gate-back" type="button" (click)="restart()">
                            Use a different email
                        </button>
                    }
                </form>
            </div>
        }
    `,
    styles: [
        `
            .env-gate {
                position: fixed;
                inset: 0;
                z-index: 10000;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 1.5rem;
                background: var(--wa-color-surface-default, #f4f4f5);
            }
            .env-gate__card {
                display: flex;
                width: 100%;
                max-width: 24rem;
                flex-direction: column;
                padding: 2rem;
                border: 1px solid var(--wa-color-neutral-border-normal, #d4d4d8);
                border-radius: 0.75rem;
                background: var(--wa-color-surface-raised, #ffffff);
                box-shadow: 0 8px 24px rgb(0 0 0 / 8%);
            }
            .env-gate__title {
                margin: 0 0 0.5rem;
                font-size: 1.125rem;
                font-weight: 600;
                color: var(--wa-color-text-normal, #18181b);
            }
            .env-gate__body {
                margin: 0 0 1.5rem;
                font-size: 0.875rem;
                line-height: 1.375rem;
                color: var(--wa-color-text-quiet, #52525b);
            }
            .env-gate__label {
                margin-bottom: 0.375rem;
                font-size: 0.8125rem;
                font-weight: 500;
                color: var(--wa-color-text-normal, #18181b);
            }
            .env-gate__input {
                padding: 0.5rem 0.75rem;
                border: 1px solid var(--wa-color-neutral-border-normal, #d4d4d8);
                border-radius: 0.375rem;
                background: var(--wa-color-surface-default, #ffffff);
                color: var(--wa-color-text-normal, #18181b);
                font: inherit;
                font-size: 0.875rem;
            }
            .env-gate__error {
                margin: 0.5rem 0 0;
                font-size: 0.8125rem;
                color: var(--wa-color-danger-60, #b91c1c);
            }
            .env-gate__notice {
                margin: 0.5rem 0 0;
                font-size: 0.8125rem;
                color: var(--wa-color-text-quiet, #52525b);
            }
            .env-gate__submit {
                margin-top: 1.25rem;
                padding: 0.5rem 1rem;
                border: none;
                border-radius: 0.375rem;
                background: var(--wa-color-brand-fill-loud, #2563eb);
                color: #ffffff;
                font: inherit;
                font-size: 0.875rem;
                font-weight: 500;
                cursor: pointer;
            }
            .env-gate__submit:disabled {
                cursor: default;
                opacity: 0.6;
            }
            .env-gate__back {
                margin-top: 0.75rem;
                border: none;
                background: none;
                color: var(--wa-color-text-quiet, #52525b);
                font: inherit;
                font-size: 0.8125rem;
                text-decoration: underline;
                cursor: pointer;
            }
        `,
    ],
})
export class EnvGate implements OnInit {
    private readonly http = inject(HttpClient);

    /** Starts up on a build that may be gated, so the app never paints in front
     *  of the reader while `/env-access/status` is in flight. The overlay is
     *  opaque and `position: fixed; inset: 0`, so the app booting behind it is
     *  simply not seen — no route, guard or `@if` around the router outlet has
     *  to know the gate exists, which is the property that made this an overlay
     *  in the first place.
     */
    protected readonly visible = signal(MAYBE_GATED_BUILDS.includes(environment.environmentType));
    /** `checking` renders the card without a form: an already-unlocked browser
     *  must not be shown an email prompt it does not need for the one frame
     *  before the status call answers.
     */
    protected readonly step = signal<'checking' | 'email' | 'code'>('checking');
    protected readonly email = signal('');
    protected readonly busy = signal(false);
    protected readonly error = signal<string | null>(null);
    protected readonly notice = signal<string | null>(null);

    async ngOnInit(): Promise<void> {
        try {
            const status = await firstValueFrom(this.http.get<EnvAccessStatus>(`${environment.apiBase}/env-access/status`));
            const gated = status?.required === true && status?.unlocked !== true;
            this.visible.set(gated);
            if (gated) {
                this.step.set('email');
            }
        } catch {
            // Fail-permissive on the DISPLAY only: if this call fails we do not
            // know whether a gate exists, and guessing "yes" would put a
            // password screen in front of an environment that has none. The
            // backend's 511 is the real enforcement, so nothing is exposed.
            this.visible.set(false);
        }
    }

    protected restart(): void {
        this.step.set('email');
        this.error.set(null);
        this.notice.set(null);
    }

    protected async submit(event: Event): Promise<void> {
        event.preventDefault();
        if (this.busy()) {
            return;
        }
        const form = event.target as HTMLFormElement;
        this.error.set(null);
        this.notice.set(null);

        if (this.step() === 'email') {
            const email = (form.elements.namedItem('email') as HTMLInputElement | null)?.value?.trim() ?? '';
            if (!email) {
                this.error.set('Enter your work email.');
                return;
            }
            await this.run(async () => {
                await firstValueFrom(this.http.post(`${environment.apiBase}/env-access/request-code`, { email }));
                this.email.set(email);
                this.step.set('code');
                this.notice.set('A six-digit code is on its way.');
            });
            return;
        }

        const code = (form.elements.namedItem('code') as HTMLInputElement | null)?.value?.trim() ?? '';
        if (!code) {
            this.error.set('Enter the six-digit code.');
            return;
        }
        await this.run(async () => {
            await firstValueFrom(this.http.post(`${environment.apiBase}/env-access/verify`, { email: this.email(), code }));
            // Reload rather than just hiding: the app's boot requests already
            // failed with 511 and services cache those failures. One line, and
            // it leaves no half-initialised state.
            window.location.reload();
        });
    }

    /** Run an action with the busy flag set, surfacing the backend's reason. */
    private async run(action: () => Promise<void>): Promise<void> {
        this.busy.set(true);
        try {
            await action();
        } catch (err: unknown) {
            const status = err instanceof HttpErrorResponse ? err.status : 0;
            this.error.set(
                status === 429
                    ? 'Too many attempts. Wait a few minutes and try again.'
                    : extractErrorDetails(err, 'Something went wrong. Please try again.')
            );
        } finally {
            this.busy.set(false);
        }
    }
}
